import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Accordion, AccordionDetails, AccordionSummary, Button, Container, Fab, Grid, Icon, makeStyles, Typography } from '@material-ui/core';
import { ArrowBackOutlined, ArrowForwardOutlined, ArrowUpward, CheckCircle, ExpandMore, HelpOutline } from '@material-ui/icons';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import { filter } from 'rxjs/operators';

import PersonalizedVideoUploadOptions from '../../components/peronalizedVideoUploadsOptions/PersonalizedVideoUploadOptions';
import FolderLinkForm from '../../components/personalizedVideo/FolderLinkForm';
import PersonalizedVideoAssistance from '../../components/personalizedVideoAssistance/PersonalizedVideoAssistance';
import PersonalizedVideoButtons from '../../components/personalizedVideoButtons/PersonalizedVideoButtons';
import PersonalizedVideoFaq from '../../components/personalizedVideoFaq/PersonalizedVideoFaq';
import PersonalizedVideoFilesRadiologyFiles from '../../components/personalizedVideoFiles/PersonalizedVideoFilesRadiologyFiles';
import PersonalizedVideoFilesRadiologyReport from '../../components/personalizedVideoFiles/PersonalizedVideoFilesRadiologyReport';
import PersonalizedVideoForm from '../../components/personalizedVideoForm/PersonalizedVideoForm';
import PersonalizedVideoFormQuestion from '../../components/personalizedVideoForm/PersonalizedVideoFormQuestion';
import PersonalizedVideoOnlinePortalAccess from '../../components/personalizedVideoOnlinePortalAccess/PersonalizedVideoOnlinePortalAccess';
import PersonalizedVideoSelectStudy from '../../components/personalizedVideoSelectStudy/PersonalizedVideoSelectStudy';
import { UPLOAD_OPTIONS } from '../../constants/general';
import { debounce, throttle } from '../../utils/lodashUtils';
import useBlocRef from '../../utils/useBlocRef.hook';

const useStyles = makeStyles((theme) => ({
  accordionDescriptionTypography: {
    color: '#999999',
    fontSize: '15px',
    fontWeight: 400,
    lineHeight: '24px',
    margin: '12px 0 0',
    textAlign: 'left',
  },
  accordionDisabled: {
    borderRadius: '8px',
  },
  accordionIconWrapper: {
    '&.Mui-expanded': {
      transform: 'rotate(0deg)',
    },
    transform: 'rotate(0deg)',
  },
  accordionParentContainer: {
    marginTop: '20px',
  },
  accordionRoot: {
    '&.MuiAccordion-root:before': {
      backgroundColor: '#00000000',
    },
  },
  accordionSummary: {
    background: '#F5F5F5',
    borderRadius: '8px',
    marginTop: '16px',
  },
  accordionSummaryContent: {
    '&.Mui-expanded': {
      margin: '16px 0',
    },
    margin: '16px 0',
  },
  accordionTypography: {
    color: '#172B4D',
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '24px',
    margin: 0,
  },
  accordionTypographyCompleted: {
    color: theme.palette.primary.main,
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '24px',
    margin: 0,
  },

  backDashboardDesktop: {
    alignItems: 'center',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    display: 'none',
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '16px',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  backDashboardMobile: {
    alignItems: 'center',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    display: 'flex',
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '16px',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  backDashboardMobileContainer: {
    display: 'block',
    marginBottom: '20px',
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  backToTopContainer: {
    alignItems: 'center',
    bottom: '92px',
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    right: '20px',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  buttonSaveProceed: {
    marginBottom: '20px',
    marginTop: '28px',
    padding: '12px 12px',
    width: '100%',

    [theme.breakpoints.up('sm')]: {
      flexGrow: 0,
      padding: '12px 24px',
      width: 'auto',
    },
  },
  container: {
    flexWrap: 'wrap',
    marginBottom: '30px',
    [theme.breakpoints.up('md')]: {
      flexWrap: 'nowrap',
      marginBottom: '0px',
    },
  },
  contentContainer: {
    padding: '20px 20px 0px',
    [theme.breakpoints.up('md')]: {
      padding: '30px 20px 50px 60px',
    },
  },
  helpIconContainer: {
    boxShadow: '0px 0px 10px 0px #A5A5A540',
    padding: '12px',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  iconLeftArrow: {
    height: '24px',
    transform: 'rotate(180deg)',
    width: '24px',
  },
  iconRightArrow: {
    height: '24px',
    width: '24px',
  },
  leftContainer: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      flexGrow: '1',
      width: 'auto',
    },
  },
  needAssistanceMobile: {
    display: 'block',
    marginTop: '60px',
    textAlign: 'left',
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },
  optionContainerLeft: {
    paddingRight: '0px',
    [theme.breakpoints.up('lg')]: {
      paddingRight: '10px',
    },
  },
  optionContainerRight: {
    paddingLeft: '0px',
    [theme.breakpoints.up('lg')]: {
      paddingLeft: '10px',
    },
  },
  personalDetailsContainer: {
    margin: '30px 0px 10px',
    textAlign: 'left',
  },
  personalizedVideoReportContainer: {
    marginTop: '50px',
    [theme.breakpoints.up('lg')]: {
      marginTop: '0px',
    },
  },
  questionContainer: {
    marginBottom: '10px',
    marginTop: '100px',
    textAlign: 'left',
  },
  radiologyFilesContainer: {
    marginBottom: '20px',
    marginTop: '40px',
    textAlign: 'left',
  },
  rightContainer: {
    borderTop: '1px solid #AAA',
    boxShadow: 'none',
    marginTop: '36px',
    minWidth: '100%',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      borderTop: 'none',
      boxShadow: '0px 0px 50px rgba(0, 0, 0, 0.1)',
      marginTop: '0',
      minWidth: '330px',
      width: '330px',
    },
    [theme.breakpoints.up('lg')]: {
      minWidth: '400px',
      width: '400px',
    },
  },
  rightContentWrapper: {
    padding: '30px 20px',
    textAlign: 'left',
  },
  rightFaqTitle: {
    color: '#172B4D',
    fontSize: '20px',
    fontWeight: '700',
    lineHeight: '30px',
    marginBottom: '5px',
    marginTop: '70px',
  },
  sectionSubtitle: {
    color: '#999999',
    fontSize: '14px',
    lineHeight: '21px',
    marginBottom: '0px',
  },
  sectionTitle: {
    color: '#172B4D',
    fontSize: '20px',
    fontWeight: '600',
    lineHeight: '24px',
    marginBottom: '4px',
  },
  submitContainer: {
    marginTop: '60px',
    width: '100%',
  },
  titleContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  typographySubtitle: {
    color: '#999999',
    fontSize: '14px',
    marginBottom: '400',
    marginTop: '4px',
  },
  typographyTitle: {
    color: '#172B4D',
    fontSize: '24px',
    fontWeight: '600',
    lineHeight: '36px',
    marginBottom: '0px',
    maxWidth: '300px',
    [theme.breakpoints.up('sm')]: {
      maxWidth: 'none',
    },
  },
}));

export default () => {
  const classes = useStyles();
  const history = useHistory();
  const blocRef = useBlocRef('personalizedVideoReportBloc');
  const [status, setStatus] = useState({
    filesStatus: {
      files: [],
      hasReport: false,
      imageCount: 0,
      reportUploaded: false,
      study: null,
      uploaded: false,
      uploadedReport: null,
    },
    formOnlineFolderLink: {
      onlineServiceName: '',
      sharingLink: '',
      studyDescription: '',
    },
    formOnlinePortalAccess: {
      portalAddress: '',
      portalPassword: '',
      portalUsername: '',
      studyName: '',
    },
    formStatus: {
      conditionChecked: false,
      dateOfBirth: null,
      gender: null,
      phoneNumber: null,
      preferredName: '',
      question: '',
      reportDate: null,
    },
    imageUploadOption: UPLOAD_OPTIONS.UPLOAD_SELF,
  });

  const [expandedAccordion, setExpandedAccordion] = useState('personal-details');
  const [studies, setStudies] = useState([]);
  const [selectedStudyId, setSelectedStudyId] = useState(null);
  const [submitButtonError, setSubmitButtonError] = useState(null);
  const [shouldRedirectToDashboard, setShouldRedirectToDashboard] = useState(false);
  const [shouldShowScrollToTop, setShouldShowScrollToTop] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const { search } = useLocation();
  const { studyId } = queryString.parse(search);

  const accordionPersonalDetailsRef = useRef();
  const accordionFilesRef = useRef();
  const accordionQuestionsRef = useRef();
  const accordionFinalizeRef = useRef();
  const mobileHelpRef = useRef();
  const debounceScrollToRef = useRef();
  const preventPositionUpdateRef = useRef(true);
  const throttleWindowScroll = useRef();

  const throttledWindowScroll = useCallback(() => {
    if (throttleWindowScroll.current) throttleWindowScroll.current.cancel();

    throttleWindowScroll.current = throttle(() => {
      if (window.scrollY < 212) {
        setShouldShowScrollToTop(false);
        return;
      }

      setShouldShowScrollToTop(true);
    }, 500);

    throttleWindowScroll.current();
  }, []);

  const debouncedScroll = useCallback((targetAccordion) => {
    if (debounceScrollToRef.current) debounceScrollToRef.current.cancel();

    debounceScrollToRef.current = debounce(() => {
      let selectedRef;
      if (targetAccordion) {
        if (targetAccordion === 'personal-details') selectedRef = accordionPersonalDetailsRef;
        if (targetAccordion === 'files') selectedRef = accordionFilesRef;
        if (targetAccordion === 'questions') selectedRef = accordionQuestionsRef;
        if (targetAccordion === 'finalize') selectedRef = accordionFinalizeRef;
      }

      if (selectedRef?.current?.getBoundingClientRect) {
        if (preventPositionUpdateRef.current) {
          preventPositionUpdateRef.current = false;
          return;
        }

        const bodyRect = document.body.getBoundingClientRect();
        const elRect = selectedRef.current.getBoundingClientRect();

        window.scrollTo({ behavior: 'smooth', top: elRect.top - bodyRect.top - 16 });
      }
    }, 500);

    debounceScrollToRef.current();
  }, []);

  useEffect(() => {
    const statusSub = blocRef.current.status$.subscribe(setStatus);
    const uploadSub = blocRef.current.uploadResult$.pipe(filter((e) => e)).subscribe(() => history.push('/patient-dashboard'));
    const paymentSub = blocRef.current.paymentResult$.pipe(filter((e) => e)).subscribe((paymentStudyId) => {
      if (paymentStudyId) {
        window.scrollTo({ behavior: 'smooth', top: 0 });
        history.push(`/package-plans?studyId=${paymentStudyId}`);
      }
    });
    const studiesSub = blocRef.current.studies$.subscribe(setStudies);
    const selectedStudyIdSub = blocRef.current.selectedStudyId$.subscribe(setSelectedStudyId);
    const submitButtonErrorSub = blocRef.current.submitButtonError$.subscribe(setSubmitButtonError);
    const shouldRedirectToDashboardSub = blocRef.current.shouldRedirectToDashboard$.subscribe(setShouldRedirectToDashboard);
    const workspaceSub = blocRef.current.userWorkspace$.subscribe(setWorkspace);

    blocRef.current.initForm();

    if (studyId) {
      blocRef.current.initStudiesData(studyId);
    } else blocRef.current.initStudiesData(undefined);

    window.addEventListener('scroll', () => throttledWindowScroll());

    return () => {
      paymentSub.unsubscribe();
      workspaceSub.unsubscribe();
      submitButtonErrorSub.unsubscribe();
      statusSub.unsubscribe();
      uploadSub.unsubscribe();
      studiesSub.unsubscribe();
      selectedStudyIdSub.unsubscribe();
      shouldRedirectToDashboardSub.unsubscribe();

      window.removeEventListener('scroll', () => throttledWindowScroll());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    blocRef.current.setSubmitButtonError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.imageUploadOption]);

  useEffect(() => {
    blocRef.current.manageSubmitButtonError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.filesStatus]);

  useEffect(() => {
    debouncedScroll(expandedAccordion);
  }, [expandedAccordion, debouncedScroll]);

  useEffect(() => {
    if (shouldRedirectToDashboard) history.push('/patient-dashboard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRedirectToDashboard]);

  useEffect(() => {
    const accordionHash = expandedAccordion ? `#${expandedAccordion}` : '';
    if (window.location.hash !== accordionHash) {
      history.push({ hash: expandedAccordion });
    }
  }, [history, expandedAccordion]);

  const handleOpenDatePopup = () => blocRef.current.openPreferredDate();
  const handleRemoveFiles = () => blocRef.current.removeFiles();
  const handleAddStorage = () => history.push('/patient-dashboard');
  const handleSubmit = async () => {
    await blocRef.current.sendCase();
  };

  const handleScrollToTop = () => window.scrollTo({ behavior: 'smooth', top: 0 });
  const handleReportUpload = (files) => blocRef.current.uploadReports(files);
  const handleRemoveReport = () => blocRef.current.removeReport();
  const handleOpenChangeTitle = () => blocRef.current.openChangeTitle();
  const handleOpenUploadReportDialog = () => blocRef.current.openUploadReportDialog();
  const handleCancel = () => history.push('/patient-dashboard');
  const handleChangeFormStatus = (value, field, validForm) => {
    blocRef.current.updateFormStatus(value, field, validForm);
  };
  const handleChangeFormOnlinePortalAccess = (value, field, validForm) => {
    blocRef.current.updateFormOnlinePortalAccess(value, field, validForm);
  };
  const handleFolderLink = (value, field, validForm) => {
    blocRef.current.updateOnlineFolderLink(value, field, validForm);
  };
  const handleImageUploadOptionChange = (value) => {
    if (status.imageUploadOption === UPLOAD_OPTIONS.USE_EXISTING_STUDY) handleRemoveFiles();
    if (value === UPLOAD_OPTIONS.USE_EXISTING_STUDY && selectedStudyId) {
      handleRemoveFiles();
      blocRef.current.populateStudy(selectedStudyId);
    }
    blocRef.current.updateImageUploadOption(value);
  };
  const handleSelectStudy = (stdyId) => {
    blocRef.current.onSelectStudy(stdyId);
  };
  const handleFilesUpload = (files) => {
    blocRef.current.onFilesUpload(files);
  };
  const handleAccordionChange = (target, expanded) => {
    setExpandedAccordion(expanded ? target : false);
  };
  const handleGotoHelp = () => {
    const bodyRect = document.body.getBoundingClientRect();
    const elRect = mobileHelpRef.current.getBoundingClientRect();

    window.scrollTo({ behavior: 'smooth', top: elRect.top - bodyRect.top });
  };
  const handleOpenImageSelectionDialog = () => {
    blocRef.current.openImageSelectionDialog();
  };

  const personalDetailsCompleted = useMemo(() => {
    if (!status.isPersonalDetailsCompleted) return false;
    return status.isPersonalDetailsCompleted();
  }, [status]);

  const fileDetailsCompleted = useMemo(() => {
    if (!status.isFileDetailsCompleted || !personalDetailsCompleted) return false;

    return status.isFileDetailsCompleted(workspace);
  }, [status, personalDetailsCompleted, workspace]);

  const isQuestionsCompleted = useMemo(() => {
    if (!fileDetailsCompleted) return false;
    return !!(status.formStatus.question || '').trim() || expandedAccordion === 'finalize';
  }, [status, fileDetailsCompleted, expandedAccordion]);

  const isFinalizeAccordionEnabled = useMemo(() => {
    return expandedAccordion === 'questions' || expandedAccordion === 'finalize';
  }, [expandedAccordion]);

  const workspaceMainFileType = useMemo(() => {
    return workspace?.mainFileType || 'dicom';
  }, [workspace]);

  return (
    <Grid container className={classes.container}>
      <Grid item className={classes.leftContainer}>
        <Container className={classes.contentContainer}>
          {/* Back to dashboard (mobile) */}
          <Grid container>
            <Grid item xs={12}>
              <div className={`text-left ${classes.backDashboardMobileContainer}`}>
                <div style={{ display: 'inline-block' }}>
                  <Typography onClick={handleCancel} className={classes.backDashboardMobile}>
                    <ArrowBackOutlined /> Back to Dashboard
                  </Typography>
                </div>

                <Icon onClick={handleGotoHelp} color="primary" className={classes.helpIconContainer}>
                  <HelpOutline />
                </Icon>
              </div>
            </Grid>

            {/* Header title */}
            <Grid item xs={12}>
              <div className={classes.titleContainer}>
                <div>
                  <Typography variant="h4" className={`${classes.typographyTitle}`} align="left">
                    Order Video Report
                  </Typography>

                  <Typography variant="body1" className={`${classes.typographySubtitle}`} align="left">
                    Please complete the sections below in order for us to create a personalized video report of your radiology results.
                  </Typography>
                </div>

                <Typography onClick={handleCancel} className={classes.backDashboardDesktop}>
                  Back to Dashboard <ArrowForwardOutlined />
                </Typography>
              </div>
            </Grid>

            {/* Personal Details Accordion */}
            <Grid item xs={12} className={classes.accordionParentContainer}>
              <Accordion
                classes={{ disabled: classes.accordionDisabled }}
                expanded={expandedAccordion === 'personal-details'}
                onChange={(_e, expanded) => handleAccordionChange('personal-details', expanded)}>
                <AccordionSummary
                  ref={accordionPersonalDetailsRef}
                  classes={{ content: classes.accordionSummaryContent, expandIcon: personalDetailsCompleted && classes.accordionIconWrapper, root: classes.accordionSummary }}
                  expandIcon={personalDetailsCompleted ? <CheckCircle color="primary" /> : <ExpandMore />}>
                  <Typography className={personalDetailsCompleted ? classes.accordionTypographyCompleted : classes.accordionTypography}>Personal Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container>
                    <Grid item xs={12} md={12} lg={8} xl={7}>
                      <PersonalizedVideoForm status={status} onOpenDatePopup={handleOpenDatePopup} onChangeFormStatus={handleChangeFormStatus} />
                    </Grid>

                    <Grid item xs={12} className="text-left">
                      <Button
                        disabled={!personalDetailsCompleted}
                        variant="contained"
                        className={classes.buttonSaveProceed}
                        color="primary"
                        onClick={() => handleAccordionChange('files', true)}>
                        Save & Proceed
                      </Button>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedAccordion === 'files'}
                disabled={!personalDetailsCompleted}
                onChange={(_e, expanded) => handleAccordionChange('files', expanded)}
                classes={{ disabled: classes.accordionDisabled, root: classes.accordionRoot }}>
                <AccordionSummary
                  ref={accordionFilesRef}
                  classes={{ content: classes.accordionSummaryContent, expandIcon: fileDetailsCompleted && classes.accordionIconWrapper, root: classes.accordionSummary }}
                  expandIcon={fileDetailsCompleted ? <CheckCircle color="primary" /> : <ExpandMore />}>
                  <Typography className={fileDetailsCompleted ? classes.accordionTypographyCompleted : classes.accordionTypography}>Your Files</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography className={classes.accordionDescriptionTypography}>
                        For us to produce a video report, we will require the files from your scan as well as your radiology report.
                        <b>We provide multiple methods for getting these files to us, please select the most convenient option for you.</b>
                      </Typography>
                    </Grid>

                    <Grid item xs={12} className={classes.optionContainerLeft}>
                      <PersonalizedVideoUploadOptions studies={studies} imageUploadOption={status.imageUploadOption} onImageUploadOptionChange={handleImageUploadOptionChange} />

                      {status.imageUploadOption === UPLOAD_OPTIONS.UPLOAD_SELF && (
                        <PersonalizedVideoFilesRadiologyFiles
                          filesStatus={status.filesStatus}
                          hideFileControls={studyId}
                          onChangeTitle={handleOpenChangeTitle}
                          onRemoveFiles={handleRemoveFiles}
                          onFilesUpload={handleFilesUpload}
                          onOpenImageSelectionDialog={handleOpenImageSelectionDialog}
                          workspaceMainFileType={workspaceMainFileType}
                        />
                      )}

                      {status.imageUploadOption === UPLOAD_OPTIONS.USE_EXISTING_STUDY && (
                        <PersonalizedVideoSelectStudy studies={studies} onSelectStudy={handleSelectStudy} selectedStudyId={selectedStudyId} />
                      )}

                      {status.imageUploadOption === UPLOAD_OPTIONS.ONLINE_PORTAL_ACCESS && (
                        <PersonalizedVideoOnlinePortalAccess status={status} onChangeFormOnlinePortalAccess={handleChangeFormOnlinePortalAccess} />
                      )}

                      {status.imageUploadOption === UPLOAD_OPTIONS.FOLDER_SHARE_LINK && <FolderLinkForm status={status} onChangeFormOnlineFolderLink={handleFolderLink} />}

                      {!status.filesStatus.hasReport && (
                        <PersonalizedVideoFilesRadiologyReport
                          onAddStorage={handleAddStorage}
                          filesStatus={status.filesStatus}
                          onRemoveReport={handleRemoveReport}
                          onReportUpload={handleReportUpload}
                          onOpenRadiologyReportDialog={handleOpenUploadReportDialog}
                          radiologyReportRequired={workspace?.radiologyReportRequired}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} className="text-left">
                      <Button
                        disabled={!fileDetailsCompleted}
                        variant="contained"
                        className={classes.buttonSaveProceed}
                        color="primary"
                        onClick={() => handleAccordionChange('questions', true)}>
                        Save & Proceed
                      </Button>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedAccordion === 'questions'}
                disabled={!fileDetailsCompleted}
                onChange={(_e, expanded) => handleAccordionChange('questions', expanded)}
                classes={{ disabled: classes.accordionDisabled, root: classes.accordionRoot }}>
                <AccordionSummary
                  ref={accordionQuestionsRef}
                  classes={{ content: classes.accordionSummaryContent, expandIcon: fileDetailsCompleted && classes.accordionIconWrapper, root: classes.accordionSummary }}
                  expandIcon={isQuestionsCompleted ? <CheckCircle color="primary" /> : <ExpandMore />}>
                  <Typography className={isQuestionsCompleted ? classes.accordionTypographyCompleted : classes.accordionTypography}>Your Questions</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography className={classes.accordionDescriptionTypography}>
                        If you have any specific questions about your findings that you would like clarified, ask them here.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <PersonalizedVideoFormQuestion status={status} onChangeFormStatus={handleChangeFormStatus} />
                    </Grid>

                    <Grid item xs={12} className="text-left">
                      <Button variant="contained" className={classes.buttonSaveProceed} color="primary" onClick={() => handleAccordionChange('finalize', true)}>
                        Save & Proceed
                      </Button>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedAccordion === 'finalize'}
                disabled={!isFinalizeAccordionEnabled}
                onChange={(_e, expanded) => handleAccordionChange('finalize', expanded)}
                classes={{ disabled: classes.accordionDisabled, root: classes.accordionRoot }}>
                <AccordionSummary ref={accordionFinalizeRef} classes={{ content: classes.accordionSummaryContent, root: classes.accordionSummary }} expandIcon={<ExpandMore />}>
                  <Typography className={classes.accordionTypography}>Finalize</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container>
                    <Grid item xs={12}>
                      <PersonalizedVideoButtons
                        status={status}
                        onChangeFormStatus={handleChangeFormStatus}
                        onCancel={handleCancel}
                        onSubmit={handleSubmit}
                        isSubmitting$={blocRef.current.isSubmitting$}
                        submitButtonError={submitButtonError}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      {/* Right content */}
      <Grid item ref={mobileHelpRef} className={classes.rightContainer}>
        <div className={classes.rightContentWrapper}>
          {/* Need assistance */}
          <PersonalizedVideoAssistance />

          {/* FAQ */}
          <div id="faq">
            <Typography className={classes.rightFaqTitle}>FAQ</Typography>
            <PersonalizedVideoFaq />
          </div>

          {shouldShowScrollToTop && (
            <Fab onClick={handleScrollToTop} color="primary" className={classes.backToTopContainer}>
              <ArrowUpward />
            </Fab>
          )}
        </div>
      </Grid>
    </Grid>
  );
};
